import { useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function BeforeAfterSlider({
  before,
  after,
  beforeAlt = "Prima",
  afterAlt = "Dopo",
  beforeLabel = "Prima",
  afterLabel = "Dopo",
  ratio = "1 / 1",
  annotations = [],
}) {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    ref.current?.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
  };

  return (
    <div
      ref={ref}
      role="slider"
      tabIndex={0}
      aria-label="Confronta prima e dopo"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      data-testid="before-after-slider"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerLeave={stop}
      onKeyDown={onKeyDown}
      className="relative w-full select-none overflow-hidden bg-black border border-ink/10 outline-none"
      style={{ aspectRatio: ratio, touchAction: "none", cursor: "ew-resize" }}
    >
      <img src={after} alt={afterAlt} draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt={beforeAlt} draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      </div>

      <span className="absolute top-4 left-4 z-10 bg-black/55 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-white/85 pointer-events-none">{beforeLabel}</span>
      <span className="absolute top-4 right-4 z-10 bg-black/55 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-white/85 pointer-events-none">{afterLabel}</span>

      <div className="absolute top-0 bottom-0 z-10 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-[2px] h-full bg-white/80 mx-auto" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 shadow-lg flex items-center justify-center">
          <ChevronLeft className="w-4 h-4 text-graphite -mr-1" />
          <ChevronRight className="w-4 h-4 text-graphite -ml-1" />
        </div>
      </div>

      {annotations.length > 0 && (
        <div className="absolute inset-0 z-[5] pointer-events-none">
          {annotations.map((a, i) => (
            <span
              key={i}
              data-testid={`annotation-${i}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
              style={{ left: `${a.x}%`, top: `${a.y}%` }}
              aria-hidden="true"
            >
              <span className="absolute w-7 h-7 rounded-full bg-sage/30 animate-ping" />
              <span className="relative w-5 h-5 rounded-full bg-sage/90 ring-2 ring-white/90 shadow flex items-center justify-center text-[10px] font-semibold text-white leading-none">
                {i + 1}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
